// Per-client REST API keys.
//
// Stored encrypted server-side and referenced by name from nodes (restRequestNode's `auth`).
// Secrets are write-only: responses only ever carry a masked value.
import * as config from "../config.js";
import { buildClientApiKeyPayload, normalizeClientApiKeys } from "../utils/clientApiKeys";

const baseUrl = () => `${config.REST_SERVER_URL}/admin/client-api-keys`;

/**
 * One request, with the HTTP status preserved so callers can tell 400 / 404 / 409 apart.
 * @returns {Promise<{ok: boolean, status: number, data: object}>}
 */
const apiRequest = async (method, path = "", body) => {
  let response;

  try {
    response = await fetch(`${baseUrl()}${path}`, {
      method,
      headers: { Accept: "application/json", "Content-Type": "application/json" },
      credentials: "include",
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    });
  } catch (error) {
    console.log("CLIENT API KEY REQUEST ERROR: ", error);
    return {
      ok: false,
      status: 0,
      data: { success: false, message: error?.message || "Request failed." },
    };
  }

  let text = "";
  try {
    text = await response.text();
  } catch (error) {
    return {
      ok: false,
      status: response.status,
      data: { success: false, message: `Could not read the response: ${error?.message}` },
    };
  }

  let data;
  try {
    data = text ? JSON.parse(text) : {};
  } catch (error) {
    data = {
      success: false,
      message: `Unexpected non-JSON response (HTTP ${response.status}): ${String(text).slice(0, 200)}`,
    };
  }

  return { ok: response.ok, status: response.status, data };
};

const flatten = ({ ok, status, data }) => ({
  ...data,
  success: ok && data?.success !== false,
  status,
});

/**
 * The client's stored keys (names, service, description, masked value). Admin users must pass a
 * client id; client admins are scoped to their own automatically.
 *
 * @param {object} [options]
 * @param {string|null} [options.clientId] Client public id.
 */
export const getClientApiKeys = async ({ clientId } = {}) => {
  const query = clientId ? `?client_id=${encodeURIComponent(clientId)}` : "";
  const result = flatten(await apiRequest("GET", query));

  return {
    ...result,
    keys: normalizeClientApiKeys(result),
    // The API may echo the resolved client; keep it for scoped follow-up calls (delete).
    client_id: result?.client_id ?? null,
  };
};

/**
 * Create or update a key — saving the same name updates it.
 *
 * The client is identified by its **public id** (never the numeric database id), and the request
 * carries the key material itself: `api_key`, plus the `refresh_key` when the service issues one.
 *
 * @param {object} payload
 * @param {string|null} payload.clientId The client's public id; omit for client-admins.
 * @param {string} payload.name
 * @param {string} payload.secret
 * @param {string} [payload.refreshToken]
 * @param {string} [payload.service]
 * @param {string} [payload.description]
 * @param {string} [payload.accessTokenExpiresAt] ISO-8601 UTC.
 * @param {string} [payload.refreshTokenExpiresAt] ISO-8601 UTC.
 */
export const saveClientApiKey = async ({
  clientId = null,
  name = "",
  secret = "",
  refreshToken = "",
  service = "",
  description = "",
  accessTokenExpiresAt = "",
  refreshTokenExpiresAt = "",
} = {}) => {
  const body = buildClientApiKeyPayload({
    clientId,
    name,
    secret,
    refreshToken,
    service,
    description,
    accessTokenExpiresAt,
    refreshTokenExpiresAt,
  });

  return flatten(await apiRequest("POST", "", body));
};

/**
 * Delete a stored key by name.
 *
 * @param {string} name
 * @param {object} [options]
 * @param {string|null} [options.clientId] The client's public id. Key names are scoped per client,
 *   so an unscoped delete can't find the key for admin users.
 */
export const deleteClientApiKey = async (name, { clientId = null } = {}) => {
  const query = clientId ? `?client_id=${encodeURIComponent(clientId)}` : "";
  return flatten(await apiRequest("DELETE", `/${encodeURIComponent(name ?? "")}${query}`));
};

export default getClientApiKeys;
